import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "consult-nontax-modal__header" }
const _hoisted_2 = { class: "consult-nontax-modal__header-icon" }
const _hoisted_3 = { class: "consult-nontax-modal__form" }
const _hoisted_4 = { class: "consult-nontax-modal__actions" }
const _hoisted_5 = {
  key: 0,
  class: "consult-nontax-modal__file-name"
}

import { ref, computed, watch } from 'vue';
import * as XLSX from 'xlsx';
import { crawlApi, consultBoardApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import CommonWageNontaxCompare from '@/components/wage/common/CommonWageNontaxCompare.vue';
import type {
  NontaxComparisonItem,
  NontaxComparisonTotals,
} from '@/components/wage/common/CommonWageNontaxCompare.vue';

interface WageRow {
  name: string;
  worker: string;
  time?: number;
  base?: number;
  overtime?: number;
  night?: number;
  holiday?: number;
  longTerm?: number;
  sunim?: number;
  dang?: number;
  etc?: number;
  lunch?: number;
  car?: number;
  childcare?: number;
  l_yeon?: number;
  gun?: number;
  jang?: number;
  go?: number;
  boss?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ConsultNontaxAnalysisModal',
  props: {
    modelValue: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

const loading = ref(false);
const loadingText = ref('분석 중...');
const error = ref('');
const facilityName = ref('');
const ym = ref('');
const rows = ref<WageRow[]>([]);
const fileName = ref('');
const fileRef = ref<HTMLInputElement | null>(null);
const compareRef = ref<InstanceType<typeof CommonWageNontaxCompare> | null>(null);

const taxRate = ref({
  tr_yeon: 0.045,
  tr_gun: 0.03545,
  tr_jang: 0.1295,
  tr_go: 0.009,
  tr_sil: 0.009,
  tr_san: 0.007,
});

const selectedYear = computed(() => {
  const [y] = ym.value.split('-');
  return parseInt(y, 10) || new Date().getFullYear();
});

const selectedMonth = computed(() => {
  const [, m] = ym.value.split('-');
  return parseInt(m, 10) || new Date().getMonth() + 1;
});

const rowCount = computed(() => rows.value.length);

const initYm = () => {
  const now = new Date();
  ym.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const num = (v: unknown) => {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const close = () => {
  open.value = false;
};

const pickFile = () => {
  if (!facilityName.value.trim()) {
    error.value = '기관명을 입력해주세요.';
    return;
  }
  fileRef.value?.click();
};

const parseExcel = (data: ArrayBuffer) => {
  const wb = XLSX.read(data, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet, {
    header: [
      'no', 'name', 'worker', 'time', 'base', 'overtime', 'night', 'holiday',
      'longTerm', 'dang', 'position', 'etc', 'lunch', 'car', 'childcare',
      'l_yeon', 'gun', 'jang', 'go', 'boss',
    ],
    range: 2,
  }) as Record<string, unknown>[];

  return json
    .filter((r) => r.name && r.worker)
    .map((r) => ({
      name: String(r.name || ''),
      worker: String(r.worker || '기타'),
      time: num(r.time),
      base: num(r.base),
      overtime: num(r.overtime),
      night: num(r.night),
      holiday: num(r.holiday),
      longTerm: num(r.longTerm),
      sunim: 0,
      dang: num(r.dang),
      position: num(r.position),
      etc: num(r.etc),
      lunch: num(r.lunch),
      car: num(r.car),
      childcare: num(r.childcare),
      l_yeon: num(r.l_yeon),
      gun: num(r.gun),
      jang: num(r.jang),
      go: num(r.go),
      boss: !!r.boss,
    }));
};

const fetchComparison = async () => {
  if (!facilityName.value.trim()) {
    error.value = '기관명을 입력해주세요.';
    return;
  }
  if (!ym.value) {
    error.value = '연월을 선택해주세요.';
    return;
  }
  if (rows.value.length === 0) {
    error.value = '엑셀 파일을 업로드해주세요.';
    return;
  }

  loading.value = true;
  loadingText.value = '비과세 분석 중...';
  error.value = '';

  try {
    const res = await consultBoardApi.postNontaxCompare({
      name: facilityName.value.trim(),
      ym: ym.value,
      rows: rows.value,
    });

    const data = res.data?.data;
    if (!data?.comparisonData) {
      throw new Error(res.data?.message || '분석 결과가 없습니다.');
    }

    if (data.taxRate) {
      taxRate.value = data.taxRate;
    }

    compareRef.value?.applyComparison(
      data.comparisonData as NontaxComparisonItem[],
      data.totals as NontaxComparisonTotals,
      selectedYear.value,
      selectedMonth.value,
    );
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `비과세 분석 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('비과세 분석 오류:', e);
    compareRef.value?.resetComparison();
  } finally {
    loading.value = false;
  }
};

const onFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  error.value = '';
  fileName.value = file.name;

  try {
    const buffer = await file.arrayBuffer();
    rows.value = parseExcel(buffer);
    if (rows.value.length === 0) {
      error.value = '엑셀에서 유효한 데이터를 찾을 수 없습니다.';
      return;
    }
    await fetchComparison();
  } catch (e: any) {
    error.value = `엑셀 처리 실패: ${e.message || '파일을 읽을 수 없습니다.'}`;
    console.error('엑셀 파싱 오류:', e);
  } finally {
    if (fileRef.value) fileRef.value.value = '';
  }
};

const downloadTemplate = async () => {
  try {
    await crawlApi.downloadWageDiagnosisTemplate();
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '다운로드 실패';
    alert(`엑셀 양식 다운로드 실패: ${msg}`);
    console.error('양식 다운로드 오류:', e);
  }
};

watch(open, (v) => {
  if (v) {
    error.value = '';
    if (!ym.value) initYm();
    compareRef.value?.resetComparison();
    rows.value = [];
    fileName.value = '';
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: open.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((open).value = $event)),
    "max-width": "1480",
    persistent: "",
    scrollable: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "consult-nontax-modal" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_v_icon, {
                size: "18",
                color: "white"
              }, {
                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                  _createTextVNode("mdi-chart-bar", -1)
                ]))]),
                _: 1
              })
            ]),
            _cache[5] || (_cache[5] = _createElementVNode("div", null, [
              _createElementVNode("h3", { class: "consult-nontax-modal__title" }, "임금개선 월별 분석"),
              _createElementVNode("p", { class: "consult-nontax-modal__subtitle" }, "임금대장 엑셀 업로드 → 임금개선 분석 보고서")
            ], -1)),
            _createVNode(_component_v_spacer),
            _createVNode(_component_v_btn, {
              variant: "text",
              class: "btn-close",
              onClick: close
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, null, {
                  default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                    _createTextVNode("mdi-close", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _createVNode(_component_v_card_text, { class: "consult-nontax-modal__body" }, {
            default: _withCtx(() => [
              _createVNode(LoadingOverlay, {
                show: loading.value,
                text: loadingText.value
              }, null, 8, ["show", "text"]),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_v_text_field, {
                  modelValue: facilityName.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((facilityName).value = $event)),
                  label: "기관명",
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "consult-nontax-modal__field"
                }, null, 8, ["modelValue"]),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((ym).value = $event)),
                  type: "month",
                  class: "consult-nontax-modal__month",
                  min: "2020-01",
                  max: "2050-12"
                }, null, 512), [
                  [_vModelText, ym.value]
                ])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_v_btn, {
                  variant: "flat",
                  class: "btn-reset",
                  onClick: downloadTemplate
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      size: "16",
                      class: "mr-1"
                    }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createTextVNode("mdi-download", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[7] || (_cache[7] = _createTextVNode(" 엑셀 양식 ", -1))
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  variant: "flat",
                  class: "btn-register",
                  onClick: pickFile
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      size: "16",
                      class: "mr-1"
                    }, {
                      default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                        _createTextVNode("mdi-file-excel", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[9] || (_cache[9] = _createTextVNode(" 엑셀 업로드 ", -1))
                  ]),
                  _: 1
                }),
                _createElementVNode("input", {
                  ref_key: "fileRef",
                  ref: fileRef,
                  type: "file",
                  accept: ".xlsx,.xls",
                  class: "consult-nontax-modal__file-hidden",
                  onChange: onFileChange
                }, null, 544),
                (fileName.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(fileName.value) + " (" + _toDisplayString(rowCount.value) + "건) ", 1))
                  : _createCommentVNode("", true)
              ]),
              (error.value)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 0,
                    type: "error",
                    density: "compact",
                    class: "mt-3"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(error.value), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(CommonWageNontaxCompare, {
                ref_key: "compareRef",
                ref: compareRef,
                embedded: "",
                "hide-scenario-settings": "",
                "wage-list": [],
                "tax-rate": taxRate.value,
                year: selectedYear.value,
                month: selectedMonth.value,
                "member-name": facilityName.value
              }, null, 8, ["tax-rate", "year", "month", "member-name"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})